import { useState, useMemo } from "react";
import { Plus, Trash2, Check, ChevronsUpDown, Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useProductFormStore } from "@/stores/productFormStore";
import { useRestaurantExtras } from "@/features/extras/hooks/useRestaurantExtras";
import type { RestaurantExtra, ProductExtraSelection } from "@/types";
import { cn } from "@/lib/utils";

interface SelectedExtraWithDetails extends ProductExtraSelection {
  restaurantExtra: RestaurantExtra;
}

export function ExtrasManager() {
  const { extras, addExtra, updateExtra, removeExtra } = useProductFormStore();
  const { data: restaurantExtras, isLoading } = useRestaurantExtras();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [selectedExtraId, setSelectedExtraId] = useState<number | null>(null);
  const [priceOverride, setPriceOverride] = useState<string>("");
  const [comboboxOpen, setComboboxOpen] = useState(false);

  // Map extras to include restaurant extra details
  const extrasWithDetails: SelectedExtraWithDetails[] = useMemo(() => {
    if (!restaurantExtras) return [];
    return extras
      .map((extra) => {
        const restaurantExtra = restaurantExtras.find(
          (re) => re.id === extra.restaurantExtraId
        );
        if (!restaurantExtra) return null;
        return { ...extra, restaurantExtra };
      })
      .filter((e): e is SelectedExtraWithDetails => e !== null);
  }, [extras, restaurantExtras]);

  // Get available extras (not already selected, unless editing that one)
  const availableExtras = useMemo(() => {
    if (!restaurantExtras) return [];
    const selectedIds = extras.map((e) => e.restaurantExtraId);
    return restaurantExtras.filter(
      (re) =>
        !selectedIds.includes(re.id) ||
        (editingIndex !== null &&
          extras[editingIndex]?.restaurantExtraId === re.id)
    );
  }, [restaurantExtras, extras, editingIndex]);

  const handleOpenCreate = () => {
    setSelectedExtraId(null);
    setPriceOverride("");
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (index: number) => {
    const extra = extras[index];
    setSelectedExtraId(extra.restaurantExtraId);
    setPriceOverride(
      extra.priceOverride !== undefined ? String(extra.priceOverride) : ""
    );
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingIndex(null);
    setSelectedExtraId(null);
    setPriceOverride("");
  };

  const handleSubmit = () => {
    if (!selectedExtraId) return;

    const extraData: ProductExtraSelection = {
      restaurantExtraId: selectedExtraId,
      priceOverride: priceOverride
        ? Number.parseFloat(priceOverride)
        : undefined,
    };

    if (editingIndex !== null) {
      updateExtra(editingIndex, extraData);
    } else {
      addExtra(extraData);
    }
    handleClose();
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(price);
  };

  const selectedRestaurantExtra = restaurantExtras?.find(
    (re) => re.id === selectedExtraId
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-24" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Extras</CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleOpenCreate}
              disabled={availableExtras.length === 0}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Extra
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!restaurantExtras || restaurantExtras.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-4">
              No extras available. Create extras in the{" "}
              <a href="/dash/extras" className="text-primary underline">
                Extras page
              </a>{" "}
              first.
            </p>
          ) : extrasWithDetails.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-4">
              No extras added yet. Add optional items like toppings or sides.
            </p>
          ) : (
            <div className="space-y-2">
              {extrasWithDetails.map((extra, index) => (
                <div
                  key={extra.restaurantExtraId}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div>
                    <p className="font-medium">{extra.restaurantExtra.name}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {extra.priceOverride !== undefined ? (
                        <>
                          <span className="line-through">
                            {formatPrice(
                              extra.restaurantExtra.price,
                              extra.restaurantExtra.currency
                            )}
                          </span>
                          <span className="text-foreground font-medium">
                            {formatPrice(
                              extra.priceOverride,
                              extra.restaurantExtra.currency
                            )}
                          </span>
                          <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                            Override
                          </span>
                        </>
                      ) : (
                        <span>
                          {formatPrice(
                            extra.restaurantExtra.price,
                            extra.restaurantExtra.currency
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenEdit(index)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeExtra(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingIndex !== null ? "Edit Extra" : "Add Extra"}
            </DialogTitle>
            <DialogDescription>
              Select an extra from your saved extras. Optionally set a custom
              price for this product.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Extra</Label>
              <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={comboboxOpen}
                    className="w-full justify-between"
                  >
                    {selectedRestaurantExtra
                      ? `${selectedRestaurantExtra.name} - ${formatPrice(
                          selectedRestaurantExtra.price,
                          selectedRestaurantExtra.currency
                        )}`
                      : "Select an extra..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search extras..." />
                    <CommandList>
                      <CommandEmpty>No extras found.</CommandEmpty>
                      <CommandGroup>
                        {availableExtras.map((extra) => (
                          <CommandItem
                            key={extra.id}
                            value={extra.name}
                            onSelect={() => {
                              setSelectedExtraId(extra.id);
                              setComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedExtraId === extra.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{extra.name}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatPrice(extra.price, extra.currency)}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price-override">Price Override (optional)</Label>
              <Input
                id="price-override"
                type="number"
                step="0.01"
                min="0"
                placeholder={
                  selectedRestaurantExtra
                    ? `Default: ${selectedRestaurantExtra.price.toFixed(2)}`
                    : "Enter custom price"
                }
                value={priceOverride}
                onChange={(e) => setPriceOverride(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use the default price from the extra.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!selectedExtraId}
            >
              {editingIndex !== null ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
